import React, {
  FunctionComponent, MouseEvent, useEffect, useRef, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, TooltipProvider } from '@pisaka/ui';
import { LogOut } from 'lucide-react';
import HeaderComponent from '../core/components/header/HeaderComponent';
import { AppStateTypes } from '../store/appStateTypes';
import { logoutTypeAction } from '../modules/auth/state/actions';
import { removeCookie } from '../helpers/cookie';
import { COOKIE_NAME_AUTH_TOKEN } from '../constants/cookieNamesConstants';
import { SidebarComponent } from '../core/components/sidebar/SidebarComponent';
import { MiniSidebarComponent } from '../core/components/sidebar/MiniSidebarComponent';
import { useModal } from '../hooks/useModal';
import { MODAL_NAME_AUTH } from '../constants/modalNamesConstants';
import { REGISTER_STATE_TYPE } from '../modules/auth/authTypes';
import AuthModalContainer from '../modules/auth/AuthModalContainer';
import { StatsContainer } from '../modules/stats/StatsContainer';
import { useStatsSocket } from '../hooks/useStatsSocket';
import { fetchCurrentSubscription } from '../modules/subscription/state/controller';
import { clearSubscriptionAction } from '../modules/subscription/state/actions';
import { AppDispatch } from '../store/configureStore';

export const withHeader = (Component: FunctionComponent) => () => {
  useStatsSocket();

  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const [isOpenSidebar, setIsOpenSidebar] = useState(false);

  const closeSidebar = () => setIsOpenSidebar(false);
  const toggleSidebar = () => setIsOpenSidebar((prev) => !prev);

  // Закрываем сайдбар при навигации
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  const { user, token } = useSelector((state: AppStateTypes) => state.auth) || {};
  const { currentSubscription, hasSubscription, isInitialized } = useSelector((state: AppStateTypes) => state.subscription);

  const hasFeature = (feature: string): boolean => {
    if (!hasSubscription || !currentSubscription?.plan) return false;
    return currentSubscription.plan.features.includes(feature);
  };

  useEffect(() => {
    if (token && !isInitialized) {
      dispatch(fetchCurrentSubscription(token));
    } else if (!token && isInitialized) {
      dispatch(clearSubscriptionAction());
    }
  }, [token, isInitialized, dispatch]);

  const logout = () => {
    dispatch(logoutTypeAction());
    removeCookie(COOKIE_NAME_AUTH_TOKEN);
  };

  const { t } = useTranslation();
  const { isOpenModal: isOpenAuthModal, onOpenModal } = useModal(MODAL_NAME_AUTH);

  const onOpenRegisterModal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    closeSidebar();
    onOpenModal({ selectedAuthStateType: REGISTER_STATE_TYPE });
  };

  const onOpenAuthModal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    closeSidebar();
    onOpenModal();
  };

  const refAd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (refAd && !hasFeature('no_ads')) {
      // @ts-ignore
      window.yaContextCb.push(() => {
        // @ts-ignore
        Ya.Context.AdvManager.render({
          blockId: 'R-A-3642064-6',
          type: 'floorAd',
          platform: 'touch'
        });
      });
    }
  }, [refAd, hasFeature]);

  return (
    <TooltipProvider delayDuration={300}>
      <>
      <HeaderComponent toggleSidebar={toggleSidebar} user={user} />
      <MiniSidebarComponent />
      <div className="mini-sidebar-offset">
        <Component />
      </div>
      <SidebarComponent
        isOpen={isOpenSidebar}
        onClose={closeSidebar}
        StatsContent={<StatsContainer />}
        AuthContent={user ? (
          <div className="flex flex-col gap-2 pr-6">
            <span className="truncate text-sm font-medium">{user.email}</span>
            <Button variant="outline" size="sm" onClick={logout} className="self-start gap-2">
              <LogOut className="w-3.5 h-3.5" />
              {t('auth.logout')}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <a href="#" onClick={onOpenRegisterModal} className="text-sm text-blue-500 hover:underline">{t('auth.register')}</a>
            <a href="#" onClick={onOpenAuthModal} className="text-sm text-blue-500 hover:underline">{t('auth.login')}</a>
          </div>
        )}
      />
      {!hasFeature('no_ads') && <div className="mb-15 mobile:hidden" ref={refAd} id="yandex_rtb_R-A-3642064-6" />}
      <AuthModalContainer />
      </>
    </TooltipProvider>
  );
};
